/**
 * Eine Zeile fuer einen Contract (Vertrag) in der Akte-Section „Verträge".
 *
 * Zeigt: Status-Pill, Typ, Gegenpartei, Restlaufzeit, monatlicher Betrag, Aktionsmenü.
 * Klick auf den Eintrag oeffnet das verkuppelte PropertyFile (Domain → File).
 */
import { useState } from 'react';
import { ConfirmDialog, Pill } from '@/shared/components';
import type { ActionMenuItem } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/format';
import type { ContractResponse, PropertyFileResponse } from '@/shared/api';
import { AkteRowMenu } from './AkteRowMenu';
import { contractRuntimeLabel, contractStatusDisplay } from './akteDomainPresenters';
import { AkteFileThumbnail } from './AkteFileThumbnail';

interface Props {
  contract: ContractResponse;
  onDownload: (propertyFileId: string) => void;
  onPreview?: (propertyFileId: string) => void;
  onEdit?: (contract: ContractResponse) => void;
  onCancel: (contract: ContractResponse) => void;
  onDelete: (contract: ContractResponse) => void;
  propertyId?: string;
  displayMode?: 'list' | 'preview';
  file?: PropertyFileResponse | null;
}

export function ContractRow({
  contract,
  onDownload,
  onPreview,
  onEdit,
  onCancel,
  onDelete,
  propertyId,
  displayMode = 'list',
  file,
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const status = contractStatusDisplay(contract);
  const runtime = contractRuntimeLabel(contract);
  const amount =
    contract.monthlyAmount != null
      ? `${formatCurrency(contract.monthlyAmount, contract.currency)}/Monat`
      : null;
  const primary = onPreview ?? onDownload;
  const showThumb = displayMode === 'preview' && propertyId && file;

  const items: ActionMenuItem[] = [
    ...(onPreview
      ? [
          {
            label: 'Vorschau',
            icon: 'Eye' as const,
            onClick: () => onPreview(contract.propertyFileId),
          },
        ]
      : []),
    {
      label: 'Herunterladen',
      icon: 'Download',
      onClick: () => onDownload(contract.propertyFileId),
    },
    ...(onEdit
      ? [{ label: 'Bearbeiten', icon: 'Edit' as const, onClick: () => onEdit(contract) }]
      : []),
    ...(contract.status === 'Active'
      ? [{ label: 'Kündigen', icon: 'Close' as const, onClick: () => onCancel(contract) }]
      : []),
    { separator: true },
    {
      label: 'Löschen',
      icon: 'Trash',
      destructive: true,
      onClick: () => setConfirmingDelete(true),
    },
  ];

  return (
    <li
      className={cn(
        'hover:bg-card-alt/50 -mx-2 flex items-center gap-3 rounded px-2 text-sm transition-colors',
        showThumb ? 'py-2' : 'py-1',
      )}
    >
      {showThumb && <AkteFileThumbnail propertyId={propertyId} file={file} width={48} />}
      <button
        type="button"
        onClick={() => primary(contract.propertyFileId)}
        className="flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-1 text-left sm:flex-row sm:items-center sm:gap-3"
        title={onPreview ? 'Vertrag öffnen (Vorschau)' : 'Vertragsdatei herunterladen'}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Pill variant={status.variant} size="sm">
            {status.label}
          </Pill>
          <span className="text-ink min-w-0 truncate">
            <span className="font-medium">{contract.type}</span>
            <span className="text-ink-soft"> · {contract.counterparty}</span>
          </span>
        </span>
        {/* Restlaufzeit und Betrag waren unter sm ausgeblendet — genau die zwei Werte, wegen
            derer man eine Vertragsliste überhaupt aufschlägt. */}
        <span className="text-ink-faint flex shrink-0 items-center gap-2 text-xs tabular-nums sm:ml-auto">
          <span>{runtime}</span>
          {amount && <span className="text-ink-soft font-medium">· {amount}</span>}
        </span>
      </button>
      <AkteRowMenu items={items} label={`Aktionen für ${contract.type}`} />

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Vertrag löschen?"
        description={`„${contract.type} · ${contract.counterparty}" wird samt Vertragsdatei aus der Akte entfernt. Das lässt sich nicht rückgängig machen.`}
        confirmLabel="Endgültig löschen"
        onConfirm={() => {
          setConfirmingDelete(false);
          onDelete(contract);
        }}
      />
    </li>
  );
}
